import { useAuth } from '@/context/AuthContext';
import { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';
import '@/styles/GraphStyles.scss'
import * as Sentry from '@sentry/react';

// Static components moved outside to prevent recreation on every render
const SquareDot = (props) => {
    const { cx, cy } = props;
    return (
        <rect
            x={cx - 4}
            y={cy - 4}
            width={8}
            height={8}
            fill="#F63040"
            stroke="#F63040"
            strokeWidth={2}
            rx={0}
            ry={0}
        />
    );
};

const PaidToSellerSquareDot = (props) => {
    const { cx, cy, payload } = props;
    if (payload.paidToSeller !== null && typeof payload.paidToSeller === 'number') {
        return (
            <rect
                x={cx - 4}
                y={cy - 4}
                width={8}
                height={8}
                fill="#57565b"
                stroke="#57565b"
                strokeWidth={2}
                rx={0}
                ry={0}
            />
        );
    }
    return null;
};

function formatRandAmount(value) {
    if (typeof value !== 'number') return value;
    return (
        'R ' +
        value
            .toFixed(1)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
            .replace(/\.0$/, '')
    );
}

const CustomTooltip = ({ active, payload, label, chartData }) => {
    if (active && payload && payload.length) {
        const dataRow = chartData.find(row => row.month === label);
        const arrears = dataRow?.amount;
        const paidToSeller = dataRow?.paidToSeller;
        const variance = dataRow?.arrearsMinusPaidToSeller;

        return (
            <div className="custom-recharts-tooltip">
                <div>
                    <strong style={{ color: "#F63040" }}>{label}</strong>
                </div>
                <div>
                    Arrears: {formatRandAmount(arrears)}
                </div>
                <div>
                    Paid To Seller: {paidToSeller !== null && paidToSeller !== undefined ? formatRandAmount(paidToSeller) : "N/A"}
                </div>
                <div>
                    Arrears minus Paid To Seller: {formatRandAmount(variance)}
                </div>
            </div>
        );
    }
    return null;
};

const MonthlyArrearsTable = ({ period }) => {
    const { apiFetch } = useAuth();
    const [arrearsData, setArrearsData] = useState();
    const [paidToSellerAmounts, setPaidToSellerAmounts] = useState();

    /**
     * Fetches arrears graph data from the API for the last 12 periods
     */
    const fetchArrearsGraphData = useCallback(async () => {
        try {
            const response = await apiFetch(`/api/Mda/GetArrearsForLast12Periods?Period=${period}`);
            const data = await response.json();

            if (data.success) {
                setArrearsData(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e)
        }
    }, [apiFetch, period]);

    /**
     * Fetches paid to seller amounts for 13 month range
     */
    const fetchPaidToSellerAmounts = useCallback(async () => {
        try {
            const response = await apiFetch(`/api/Leasing/PaidToSeller13MonthRange?Period=${period}`);
            const data = await response.json();
            if (data.success) {
                setPaidToSellerAmounts(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    }, [apiFetch, period]);

    useEffect(() => {
        if (!period) return;
        const timer = setTimeout(() => {
            fetchArrearsGraphData();
            fetchPaidToSellerAmounts();
        }, 0);
        return () => clearTimeout(timer);
    }, [period, fetchArrearsGraphData, fetchPaidToSellerAmounts]);

    //maps the data to the format required by Recharts
    const chartData = arrearsData
        ? Object.entries(arrearsData).map(([monthLabel, value]) => {
            const periodKey = labelToPeriod(monthLabel);
            const paid = paidToSellerAmounts && paidToSellerAmounts[periodKey] != null
                ? paidToSellerAmounts[periodKey]
                : null;
            return {
                month: monthLabel,
                amount: value,
                paidToSeller: paid,
                arrearsMinusPaidToSeller: paid !== null ? value - paid : value
            };
        })
        : [];

    function labelToPeriod(label) {
        // Handles both "Sep" and "Sept"
        const [monthStr, yearStr] = label.split(' ');
        const monthMap = {
            Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
            Jul: '07', Aug: '08', Sept: '09', Oct: '10', Nov: '11', Dec: '12'
        };
        return yearStr + (monthMap[monthStr] || '00');
    }

    // This defines the minimum and maximum Y-axis values, as well as step size for the ticks
    const minY = 10000000;
    const step = 2000000;
    const maxDataY = chartData.length
        ? Math.max(...chartData.map(d => d.amount), minY)
        : minY;
    const maxTick = Math.ceil((maxDataY - minY) / step) * step + minY;
    const ticks = [];
    for (let t = minY; t <= maxTick; t += step) {
        ticks.push(t);
    }

    const monthAbbr = {
        January: 'Jan',
        February: 'Feb',
        March: 'Mar',
        April: 'Apr',
        May: 'May',
        June: 'Jun',
        July: 'Jul',
        August: 'Aug',
        September: 'Sep', // Sept is the issue
        October: 'Oct',
        November: 'Nov',
        December: 'Dec'
    };

    return (
        <div className="graph-box" style={{ height: '311px' }}>
            <div className="graph-heading-arrears">
                Monthly Arrears
            </div>
            <div className="graph-content" style={{ height: '100%' }}>
                <ResponsiveContainer>
                    <LineChart
                        data={chartData}
                        margin={{ top: 20, right: 40, left: 60, bottom: 5 }}
                    >
                        <CartesianGrid stroke="#000" strokeDasharray="0" horizontal={true} vertical={false} fill="#f4f4f4" />
                        <XAxis
                            dataKey="month"
                            tickFormatter={label => {
                                const [fullMonth, year] = label.split(' ');
                                const abbr = monthAbbr[fullMonth] || fullMonth;
                                const shortYear = year ? year.slice(-2) : '';
                                return `${abbr} ${shortYear}`;
                            }}
                            textAnchor="end"
                            tick={{ fontSize: 11 }} 
                            interval={0}
                        />
                        <YAxis domain={[minY, maxTick]}
                            ticks={ticks}
                            label={{
                                value: '(Rand)',
                                angle: -90,
                                position: 'left', 
                                offset: 35,     
                                style: { textAnchor: 'middle', fontSize: 14, fill: '#555' }
                            }}
                        />
                        <Tooltip content={<CustomTooltip chartData={chartData} />} />
                        <Legend />
                        <Line
                            type="monotone"
                            dataKey="amount"
                            stroke="#F63040"
                            strokeWidth={2}
                            dot={<SquareDot fill="#F63040" stroke="#F63040" />}
                            name="Arrears"
                        />
                        <Line
                            type="monotone"
                            dataKey="arrearsMinusPaidToSeller"
                            stroke="#57565b"
                            strokeWidth={2}
                            dot={<PaidToSellerSquareDot />}
                            name="Normalised arrears - excl paid to seller"
                        />

                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default MonthlyArrearsTable;